import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  TextField,
  Tooltip,
} from '@mui/material';
import {
  Upload as UploadIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Download as DownloadIcon,
  Save as SaveIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../../config';

function DPRValidationDashboard({ user }) {
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [saving, setSaving] = useState(false);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/dpr_validation.php?action=download_template`,
        {
          withCredentials: true,
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'DPR_Validation_Template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading template:', error);
      alert('Failed to download template');
    }
  };

  const handleValidate = async () => {
    if (!selectedFile) {
      alert('Please select a file first');
      return;
    }

    if (!selectedDate) {
      alert('Please select a date');
      return;
    }

    setUploading(true);
    setValidationResult(null);
    
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('selected_date', selectedDate);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/dpr_validation.php?action=validate_dpr`,
        formData,
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        setValidationResult(response.data);
        setActiveTab(0);
      } else {
        alert('Validation failed: ' + (response.data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Upload failed: ' + (error.response?.data?.message || error.message));
    } finally {
      setUploading(false);
    }
  };

  const handleExportValidation = async () => {
    if (!validationResult) {
      alert('No validation data to export');
      return;
    }

    try {
      const response = await axios.post(
        `${API_BASE_URL}/dpr_validation.php?action=export_current_validation`,
        {
          validation_date: validationResult.validation_date,
          details: validationResult.details
        },
        {
          withCredentials: true,
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `DPR_Validation_${validationResult.validation_date}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export error:', error);
      alert('Export failed');
    }
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US', { 
      minimumFractionDigits: 0, 
      maximumFractionDigits: 2 
    }).format(num || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: '2-digit', 
      day: '2-digit' 
    });
  };

  const getStatusChip = (status) => {
    if (!status) return null;
    const statusStr = String(status);
    
    if (statusStr.includes('PRODUCED')) {
      return <Chip label="PRODUCED" size="small" sx={{ bgcolor: '#4caf50', color: 'white', fontWeight: 700 }} />;
    }
    if (statusStr.includes('REPACK-IN')) {
      return <Chip label="REPACK-IN" size="small" sx={{ bgcolor: '#2196f3', color: 'white', fontWeight: 700 }} />;
    }
    if (statusStr.includes('REPACK-OUT')) {
      return <Chip label="REPACK-OUT" size="small" sx={{ bgcolor: '#ff9800', color: 'white', fontWeight: 700 }} />;
    }
    if (statusStr.includes('SHIPPED')) {
      return <Chip label="SHIPPED" size="small" sx={{ bgcolor: '#9c27b0', color: 'white', fontWeight: 700 }} />;
    }
    return <Chip label={statusStr} size="small" sx={{ fontWeight: 700 }} />;
  };

  const getValidationChip = (validationStatus) => {
    switch (validationStatus) {
      case 'match':
        return <Chip label="✓ Matched" size="small" sx={{ bgcolor: '#4caf50', color: 'white', fontWeight: 700 }} />;
      case 'mismatch':
        return <Chip label="⚠ Mismatch" size="small" sx={{ bgcolor: '#ff9800', color: 'white', fontWeight: 700 }} />;
      case 'missing_in_excel':
        return <Chip label="✗ Missing" size="small" sx={{ bgcolor: '#f44336', color: 'white', fontWeight: 700 }} />;
      default:
        return <Chip label={validationStatus} size="small" sx={{ fontWeight: 700 }} />;
    }
  };

  const getShiftChip = (shift) => {
    if (shift === 'DAY') {
      return <Chip label="DAY" size="small" sx={{ bgcolor: '#2196f3', color: 'white', fontWeight: 700, minWidth: '60px' }} />;
    } else if (shift === 'NIGHT') {
      return <Chip label="NIGHT" size="small" sx={{ bgcolor: '#9c27b0', color: 'white', fontWeight: 700, minWidth: '60px' }} />;
    }
    return <Chip label={shift || '-'} size="small" sx={{ fontWeight: 700, minWidth: '60px' }} />;
  };

  // ✅ NEW: Render validation results table with Total Bags
  const renderDetailsTable = (details, title) => {
    if (!details || details.length === 0) {
      return (
        <Alert severity="info">No {title.toLowerCase()} found</Alert>
      );
    }

    return (
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#1565c0' }}>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'white' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'white' }}>Shift</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'white' }}>Product</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'white' }}>Grade</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'white' }}>Pack Size</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'white' }}>Brand</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'white', bgcolor: '#388e3c' }}>System<br/>Total Bags</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'white', bgcolor: '#1565c0' }}>Excel<br/>Total Bags</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'white', bgcolor: '#e65100' }}>Bags<br/>Diff</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'white' }}>System<br/>Cartons</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'white' }}>Excel<br/>Cartons</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'white' }}>System<br/>Loose</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'white' }}>Excel<br/>Loose</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'white' }}>Validation</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {details.map((row, index) => {
              const hasBagsDiff = Math.abs(row.total_bags_diff || 0) > 0.1;
              const hasCartonsDiff = Math.abs(row.cartons_diff || 0) > 0.01;
              const systemLoose = (row.system_loose_bags || 0) + (row.system_loose_kgs || 0);
              const excelLoose = (row.excel_loose_bags || 0) + (row.excel_loose_kgs || 0);
              
              return (
                <TableRow 
                  key={index} 
                  sx={{ 
                    '&:hover': { bgcolor: '#f5f5f5' },
                    bgcolor: row.validation_status === 'match' ? '#e8f5e9' : 
                             row.validation_status === 'mismatch' ? '#fff3e0' : '#ffebee'
                  }}
                >
                  <TableCell>{getStatusChip(row.status)}</TableCell>
                  <TableCell>{getShiftChip(row.shift)}</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.813rem' }}>{row.product}</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.813rem' }}>{row.grade}</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.813rem' }}>{row.pack_size}</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.813rem' }}>{row.brand}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.875rem', bgcolor: '#e8f5e9' }}>
                    {formatNumber(row.system_total_bags)}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.875rem', bgcolor: '#e3f2fd' }}>
                    {formatNumber(row.excel_total_bags)}
                  </TableCell>
                  <TableCell align="center" sx={{ 
                    fontWeight: 700, 
                    fontSize: '0.875rem',
                    color: hasBagsDiff ? '#d32f2f' : '#2e7d32',
                    bgcolor: hasBagsDiff ? '#ffebee' : '#e8f5e9'
                  }}>
                    {row.total_bags_diff !== undefined ? formatNumber(row.total_bags_diff) : '-'}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.813rem' }}>
                    {formatNumber(row.system_cartons)}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.813rem' }}>
                    {formatNumber(row.excel_cartons)}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.813rem' }}>
                    {formatNumber(systemLoose)}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.813rem' }}>
                    {formatNumber(excelLoose)}
                  </TableCell>
                  <TableCell>
                    <Tooltip title={
                      row.matching_repack_shipment ? (
                        <Box>
                          <Typography variant="caption" sx={{ fontWeight: 700 }}>
                            Explained by: {row.matching_repack_shipment.status}
                          </Typography>
                          <br />
                          <Typography variant="caption">
                            Cartons: {formatNumber(row.matching_repack_shipment.cartons)}
                          </Typography>
                        </Box>
                      ) : row.validation_status === 'match' ? 'Perfect match!' : 'Discrepancy found'
                    }>
                      {getValidationChip(row.validation_status)}
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // ✅ Render inventory data table (for tabs 1, 2, 3)
  const renderInventoryTable = (data, title) => {
    if (!data || data.length === 0) {
      return <Alert severity="info">No {title.toLowerCase()} data found</Alert>;
    }

    return (
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Product</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Grade</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Pack Size</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Brand</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Cartons</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Loose Bags</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Loose KGs</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Total KGs</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Total Lbs</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row, index) => (
              <TableRow key={index} sx={{ '&:hover': { bgcolor: '#f9f9f9' } }}>
                <TableCell>{getStatusChip(row.status)}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{row.product}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{row.grade}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{row.pack_size}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{row.brand}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>{formatNumber(row.total_cartons)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>{formatNumber(row.total_loose_bags)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>{formatNumber(row.total_loose_kgs)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>{formatNumber(row.total_kgs)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>{formatNumber(row.total_pounds)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 3, bgcolor: '#f8f9fa' }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#1565c0', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          📋 DPR Validation Dashboard
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
          Compare Daily Production Report (DPR) with system inventory data using Total Bags calculation
        </Typography>
      </Paper>

      {/* Upload Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          {/* Date Picker */}
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              type="date"
              label="Production Date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ bgcolor: 'white' }}
            />
          </Grid>

          {/* File Upload */}
          <Grid item xs={12} md={4}>
            <Button
              variant="outlined"
              component="label"
              fullWidth
              sx={{ 
                height: '56px',
                fontWeight: 600,
                borderStyle: 'dashed',
                borderWidth: 2
              }}
            >
              <UploadIcon sx={{ mr: 1 }} />
              {selectedFile ? selectedFile.name : 'Select DPR Excel File'}
              <input
                type="file"
                hidden
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
              />
            </Button>
          </Grid>

          {/* Validate Button */}
          <Grid item xs={12} md={2}>
            <Button
              variant="contained"
              color="primary"
              startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
              onClick={handleValidate}
              disabled={uploading || !selectedFile || !selectedDate}
              fullWidth
              sx={{ 
                fontWeight: 700,
                height: '56px'
              }}
            >
              {uploading ? 'VALIDATING...' : 'VALIDATE DPR'}
            </Button>
          </Grid>

          {/* Download Template */}
          <Grid item xs={12} md={2}>
            <Button
              variant="outlined"
              color="success"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadTemplate}
              fullWidth
              sx={{ 
                fontWeight: 600,
                height: '56px'
              }}
            >
              Download Template
            </Button>
          </Grid>

          {/* Export Button */}
          <Grid item xs={12} md={2}>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<DownloadIcon />}
              onClick={handleExportValidation}
              disabled={!validationResult}
              fullWidth
              sx={{ 
                fontWeight: 600,
                height: '56px'
              }}
            >
              Export Results
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Results Section */}
      {validationResult && (
        <>
          {/* Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} md={3}>
              <Card sx={{ borderLeft: '4px solid #4caf50' }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 1 }}>
                    Total Records
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700, color: '#4caf50' }}>
                    {validationResult.summary.total_records}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={6} md={3}>
              <Card sx={{ borderLeft: '4px solid #2196f3' }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 1 }}>
                    ✓ Matched
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700, color: '#2196f3' }}>
                    {validationResult.summary.matched}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={6} md={3}>
              <Card sx={{ borderLeft: '4px solid #ff9800' }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 1 }}>
                    ⚠ Mismatched
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700, color: '#ff9800' }}>
                    {validationResult.summary.mismatched}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={6} md={3}>
              <Card sx={{ borderLeft: '4px solid #f44336' }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 1 }}>
                    ✗ Missing in Excel
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700, color: '#f44336' }}>
                    {validationResult.summary.missing_in_excel}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Tabs */}
          <Paper sx={{ mb: 3 }}>
            <Tabs 
              value={activeTab} 
              onChange={(e, newVal) => setActiveTab(newVal)}
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab label="Validation Results" sx={{ fontWeight: 600 }} />
              <Tab label="Daily Inventory" sx={{ fontWeight: 600 }} />
              <Tab label="Repack-Out" sx={{ fontWeight: 600 }} />
              <Tab label="Shipments" sx={{ fontWeight: 600 }} />
            </Tabs>

            <Box sx={{ p: 2 }}>
              {/* Tab 0: Validation Results */}
              {activeTab === 0 && (
                <>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                    Validation Results - {formatDate(validationResult.validation_date)}
                  </Typography>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <strong>Total Bags Calculation:</strong> For LB/OZ packs: (bags per carton × cartons) + loose bags. 
                    For KG packs: cartons + (loose KGs ÷ weight per bag). Smart brand matching handles variations like "B&B" = "BOWL AND BASKET".
                  </Alert>
                  {renderDetailsTable(validationResult.details, 'Validation Results')}
                </>
              )}

              {/* Tab 1: Daily Inventory Data */}
              {activeTab === 1 && (
                <>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                    Daily Inventory Data - {formatDate(validationResult.validation_date)}
                  </Typography>
                  {renderInventoryTable(validationResult.daily_inventory, 'Daily Inventory')}
                </>
              )}

              {/* Tab 2: Repack-Out Data */}
              {activeTab === 2 && (
                <>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                    Repack-Out Data - {formatDate(validationResult.validation_date)}
                  </Typography>
                  {renderInventoryTable(validationResult.repack_out, 'Repack-Out')}
                </>
              )}

              {/* Tab 3: Shipment Data */}
              {activeTab === 3 && (
                <>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                    Shipment Data - {formatDate(validationResult.validation_date)}
                  </Typography>
                  {renderInventoryTable(validationResult.shipments, 'Shipments')}
                </>
              )}
            </Box>
          </Paper>
        </>
      )}

      {/* Instructions */}
      {!validationResult && (
        <Paper sx={{ p: 3, bgcolor: '#f5f5f5' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon color="primary" />
            How to Use
          </Typography>
          <Typography variant="body2" paragraph sx={{ fontWeight: 500 }}>
            1. <strong>Download the template</strong> using the "Download Template" button
          </Typography>
          <Typography variant="body2" paragraph sx={{ fontWeight: 500 }}>
            2. <strong>Fill in your DPR data</strong> with columns: PRODUCT, GRADE, PACK SIZE, BRAND, TOTAL BAGS, CARTONS, LOOSE
          </Typography>
          <Typography variant="body2" paragraph sx={{ fontWeight: 500 }}>
            3. <strong>Total Bags calculation:</strong>
          </Typography>
          <Box sx={{ pl: 3, mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, color: '#1565c0' }}>
              • For LB/OZ: (bags per carton × cartons) + loose bags
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, color: '#1565c0' }}>
              • Example: 10/2 LB with 50 cartons + 2 loose = (10×50) + 2 = <strong>502 bags</strong>
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, color: '#388e3c', mt: 1 }}>
              • For KG: cartons + (loose KGs ÷ weight per bag)
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, color: '#388e3c' }}>
              • Example: 1/10 KG with 5 cartons + 3kg loose = 5 + (3÷10) = <strong>5.3 bags</strong>
            </Typography>
          </Box>
          <Typography variant="body2" paragraph sx={{ fontWeight: 500 }}>
            4. <strong>LOOSE column:</strong> Enter loose bags (for LB/OZ) OR loose KGs (for KG pack sizes)
          </Typography>
          <Typography variant="body2" paragraph sx={{ fontWeight: 500 }}>
            5. <strong>Smart brand matching:</strong> Handles variations like "B&B" = "BOWL AND BASKET", extra spaces, abbreviations
          </Typography>
          <Typography variant="body2" paragraph sx={{ fontWeight: 500 }}>
            6. <strong>Select the production date</strong> and upload the filled template
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            7. <strong>Review the validation results</strong> - system compares Total Bags for accuracy
          </Typography>
        </Paper>
      )}
    </Box>
  );
}

export default DPRValidationDashboard;